import {
  FieldErrorsImpl,
  useFormContext,
  UseFormRegister,
  UseFormSetValue,
  UseFormTrigger,
  UseFormWatch,
} from 'react-hook-form';
import { useAppSelector } from 'hooks/useRedux';
import { formsSelectors } from 'state/forms';
import ErrorBadge from '../ErrorBadge/ErrorBadge';
import { ValuesType } from 'state/forms/forms.reducer';
import { FIELD_MISSING, PICK_LOCATION } from 'constants/validationMessages';
import Select from 'react-select';

export interface Props {
  label: string;
  isRequired?: boolean;
  className?: string;
  setValue?: UseFormSetValue<ValuesType>;
  watch?: UseFormWatch<ValuesType>;
  trigger?: UseFormTrigger<ValuesType>;
  errors?: Partial<
    FieldErrorsImpl<{
      [x: string]: NonNullable<string | string[]>;
    }>
  >;
  formKey: string;
  choices: any;
  register: UseFormRegister<ValuesType>;
  hideReq?: boolean;
  isDisabled?: boolean;
}
interface Choice {
  [x: string]: {
    eng: string;
  };
}

interface Option {
  value: string;
  label: string;
}

const SelectField = ({
  label,
  className,
  isRequired,
  choices,
  errors,
  formKey,
  register,
  hideReq,
  isDisabled,
}: Props) => {
  const state = useAppSelector(formsSelectors.stateSelector);
  const { trigger, setValue, watch } = useFormContext();

  const handleChange = (selected: Option | null) => {
    const value = selected ? selected.value : '';
    setValue && setValue(formKey, value, { shouldValidate: true });
    trigger && trigger(formKey);
  };

  const setHideReq = () => {
    if (hideReq) return true;
  };

  const validationBudge = errors && errors[formKey] && (
    <ErrorBadge
      errors={errors}
      formKey={formKey}
    />
  );

  const newChoices = choices.options ? [...choices.options, ...choices.staticOptions] : choices.eng;

  const options: Option[] = Array.isArray(newChoices)
    ? newChoices.map((choice: Choice) => {
        if (typeof choice === 'string') {
          return { value: choice, label: choice };
        } else {
          const key = Object.keys(choice)[0];
          return { value: key, label: choice[key].eng };
        }
      })
    : [];

  const selectedOption = options.find((opt) => opt.value === (watch ? watch(formKey) : state));

  return (
    <>
      <label data-testid="label">
        {label}
        {!setHideReq() && <span className="text-danger"> *</span>}
      </label>
      <div data-testid="selectField">
        <Select
          id={formKey}
          {...register(formKey, {
            validate: (value) => {
              if (isRequired && !value && setHideReq()) {
                return PICK_LOCATION;
              }
              if (isRequired && !value) {
                return FIELD_MISSING;
              }
            },
          })}
          options={options}
          value={selectedOption || null}
          className={`${className} `}
          classNamePrefix={'select-field'}
          onChange={handleChange}
          name={formKey}
          placeholder={`Choose ${label}`}
          menuIsOpen={process.env.NODE_ENV === 'test' ? true : undefined}
          isDisabled={isDisabled}
          components={{ IndicatorSeparator: () => null }}
          styles={{
            control: (baseStyles, { isFocused }) => ({
              ...baseStyles,
              borderRadius: '50rem',
              borderColor: isFocused ? 'rgba(13, 110, 253, 0.5)' : '#dee2e6',
              borderWidth: '2px',
              boxShadow: isFocused
                ? '0 0 0 0.25rem rgba(13, 110, 253, 0.25)'
                : baseStyles.boxShadow,
              ...(errors && errors[formKey] ? { borderColor: '#dc354580' } : {}),
              '&:hover': {
                borderColor: isFocused ? 'rgba(13, 110, 253, 0.5)' : '#dee2e6',
                cursor: isDisabled ? 'not-allowed' : 'text',
              },
            }),
            option: (baseStyles, { isFocused }) => ({
              ...baseStyles,
              backgroundColor: isFocused ? '#7d55c7' : '#fff',
              color: isFocused ? '#fff' : '#333',
              padding: '0.3rem 0.75rem',
              borderTop: '1px solid rgba(222, 226, 230, 0.35)',
              '&:first-of-type': {
                borderTop: 'none',
              },
            }),
            menu: (baseStyles) => ({
              ...baseStyles,
              borderRadius: '0.5rem',
              boxShadow: '0px 8px 16px 0px rgba(0, 0, 0, 15%)',
              marginTop: '0',
              zIndex: 9999,
            }),
            menuList: (baseStyles) => ({
              ...baseStyles,
              borderRadius: '8px',
              paddingTop: '0',
              paddingBottom: '0',
            }),
            placeholder: (base) => ({
              ...base,
              overflow: 'hidden',
              whiteSpace: 'nowrap',
              textOverflow: 'ellipsis',
            }),
          }}
          isSearchable={false}
        />

        {validationBudge}
      </div>
    </>
  );
};

export default SelectField;
